import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./VenueDetailPage.css";
import { useGetBusinessData, useUserData } from "../../../Utility";
import {
  formatDate,
  getTags,
  isBusinessUser,
  isLoggedIn,
  normalizeAmenitiesList,
  tranformBusinessData,
} from "../../../utils/UtilityFunctions";
import ReviewForm from "../../components/ReviewForm";
import ExploreMoreCities from "../../components/ExploreMoreCities";
import WvManagedBadge from "../../components/WvManagedBadge";
import { buildHomeCityPath } from "../../../utils/listingPaths";

function VenueDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("about");
  const businessData = useGetBusinessData();
  const userData = useUserData();
  const businessesList = businessData?.businessesList ?? [];
  const isEndUserLoggedIn = isLoggedIn();
  const isBusinessLoggedIn = isBusinessUser();

  const filteredData = businessesList?.filter(
    (venue) => venue.id === parseInt(id),
  );
  const venue = tranformBusinessData(filteredData, true);

  const isLoading = businessData?.getBusinessesLoading;

  if (isLoading) {
    return (
      <div className="venue-detail-page venue-detail-page-skeleton">
        <div className="venue-detail-hero">
          <div className="venue-main-image venue-detail-skeleton-main-image skeleton-pulse" />
          <div className="venue-thumbnail-grid">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="venue-detail-skeleton-thumb skeleton-pulse"
              />
            ))}
          </div>
        </div>
        <div className="venue-detail-container">
          <div className="venue-detail-header">
            <div className="venue-header-left">
              <div className="venue-detail-skeleton-badge skeleton-pulse" />
              <div className="venue-detail-skeleton-title skeleton-pulse" />
              <div className="venue-detail-skeleton-line skeleton-pulse" />
              <div className="venue-detail-skeleton-rating skeleton-pulse" />
            </div>
            <div className="venue-header-right">
              <div className="venue-detail-skeleton-price skeleton-pulse" />
              <div className="venue-detail-skeleton-btn skeleton-pulse" />
              <div className="venue-detail-skeleton-btn skeleton-pulse" />
            </div>
          </div>
          <div className="venue-details-grid">
            <div className="venue-details-main">
              <section className="venue-section">
                <div className="venue-detail-skeleton-section-title skeleton-pulse" />
                <div className="venue-detail-skeleton-line skeleton-pulse" />
                <div className="venue-detail-skeleton-line skeleton-pulse" />
                <div className="venue-detail-skeleton-line venue-detail-skeleton-line-short skeleton-pulse" />
              </section>
              <section className="venue-section">
                <div className="venue-detail-skeleton-section-title skeleton-pulse" />
                <div className="venue-detail-features-grid">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className="venue-detail-skeleton-feature skeleton-pulse"
                    />
                  ))}
                </div>
              </section>
              <section className="venue-section">
                <div className="venue-detail-skeleton-section-title skeleton-pulse" />
                <div className="amenities-grid">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div
                      key={i}
                      className="venue-detail-skeleton-amenity skeleton-pulse"
                    />
                  ))}
                </div>
              </section>
            </div>
            <div className="venue-details-sidebar">
              <div className="venue-contact-card venue-enquiry-card">
                <div className="venue-detail-skeleton-section-title skeleton-pulse" />
                <div className="venue-detail-skeleton-line skeleton-pulse" />
                <div className="venue-detail-skeleton-btn skeleton-pulse" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!venue) {
    return (
      <div className="venue-detail-page">
        <div className="venue-not-found">
          <h2>Venue not found</h2>
          <button onClick={() => navigate("/venues")} className="back-btn">
            Back to Venues
          </button>
        </div>
      </div>
    );
  }

  const handleImageClick = (index) => {
    setSelectedImageIndex(index);
    setIsImageModalOpen(true);
  };

  const handleNextImage = () => {
    setSelectedImageIndex((prev) => (prev + 1) % venue?.[0]?.images.length);
  };

  const handlePrevImage = () => {
    setSelectedImageIndex(
      (prev) =>
        (prev - 1 + venue?.[0]?.images.length) % venue?.[0]?.images.length,
    );
  };
  return (
    <div className="venue-detail-page">
      {/* Image Gallery Section */}
      <div className="venue-detail-hero">
        <div className="venue-main-image">
          <img
            src={venue?.[0]?.images[selectedImageIndex]}
            alt={venue.name}
            onClick={() => handleImageClick(selectedImageIndex)}
          />
          <button
            className="image-nav-btn prev-btn"
            onClick={handlePrevImage}
            aria-label="Previous image"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>
          <button
            className="image-nav-btn next-btn"
            onClick={handleNextImage}
            aria-label="Next image"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        </div>
        <div className="venue-thumbnail-grid">
          {venue?.[0]?.images.map((image, index) => (
            <div
              key={index}
              className={`venue-thumbnail ${index === selectedImageIndex ? "active" : ""}`}
              onClick={() => setSelectedImageIndex(index)}
            >
              <img src={image} alt={`${venue.name} ${index + 1}`} />
            </div>
          ))}
        </div>

        {/* Tabs: About | Reviews */}
        <div className="detail-tabs" role="tablist" aria-label="Venue details">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "about"}
            aria-controls="venue-about-panel"
            id="venue-tab-about"
            className={`detail-tab ${activeTab === "about" ? "active" : ""}`}
            onClick={() => setActiveTab("about")}
          >
            About
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "reviews"}
            aria-controls="venue-reviews-panel"
            id="venue-tab-reviews"
            className={`detail-tab ${activeTab === "reviews" ? "active" : ""}`}
            onClick={() => setActiveTab("reviews")}
          >
            Reviews
          </button>
        </div>
      </div>

      <div className="venue-detail-container">
        {/* Header Section */}
        <div className="venue-detail-header">
          <div className="venue-header-left">
            <div className="venue-detail-badges-row">
              {/* <div className="venue-type-badge-large">{venue?.[0]?.type}</div> */}
              <div className="venue-detail-tags-inline">
                {getTags(venue?.[0]?.tags)
                  .filter(
                    (t) =>
                      !/^(Managed|Wz Managed|Wv Managed)$/i.test(
                        String(t).trim(),
                      ),
                  )
                  .map((tag, index) => (
                    <WvManagedBadge key={index} text={tag.trim()} />
                  ))}
              </div>
            </div>
            <h1 className="venue-detail-name">{venue?.[0]?.name}</h1>
            <div className="venue-location">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
              <span>{venue?.[0]?.address}</span>
            </div>
            <div className="venue-rating">
              <div className="rating-stars">
                {[...Array(5)].map((_, i) => (
                  <svg
                    key={i}
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill={
                      i < Math.floor(venue?.[0]?.average_rating)
                        ? "#1e3a5f"
                        : "none"
                    }
                    stroke="#1e3a5f"
                    strokeWidth="2"
                  >
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                  </svg>
                ))}
              </div>
              <span className="rating-value">{venue?.[0]?.average_rating}</span>
              <span className="rating-count">
                {venue?.[0]?.reviews_list?.length ?? 0} review
                {venue?.[0]?.reviews_list?.length === 1 ? "" : "s"})
              </span>
            </div>
          </div>
          <div className="venue-header-right">
            <div className="venue-price-box">
              <span className="price-label">
                Price per plate: Starting from
              </span>
              <span className="price-value">
                <span className="venue-price-icon" aria-hidden="true">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="8" />
                    <circle cx="12" cy="12" r="5" strokeOpacity="0.35" />
                  </svg>
                </span>
                <span className="price-amount">
                  {venue?.[0]?.pricePerPlate?.discounted != null
                    ? String(venue[0].pricePerPlate.discounted).startsWith("₹")
                      ? String(venue[0].pricePerPlate.discounted)
                      : `₹${venue[0].pricePerPlate.discounted}`
                    : "—"}
                </span>
                <span className="price-unit">/ plate</span>
              </span>
            </div>
            {!isBusinessLoggedIn && (
              <button
                className="venue-enquire-btn"
                onClick={() => navigate("/enquiry?venueId=" + venue?.[0]?.id)}
              >
                Enquire Now
              </button>
            )}
          </div>
        </div>

        {/* Details Grid – tab content */}
        <div className="venue-details-grid">
          <div className="venue-details-main">
            {activeTab === "about" && (
              <>
                {/* Description */}
                <section
                  className="venue-section"
                  id="venue-about-panel"
                  role="tabpanel"
                  aria-labelledby="venue-tab-about"
                >
                  <h2 className="venue-section-title">About This Venue</h2>
                  <p className="venue-description">{venue?.[0]?.description}</p>
                </section>

                {/* Key Features */}
                <section className="venue-section">
                  <h2 className="venue-section-title">Key Features</h2>
                  <div className="venue-features-grid">
                    <div className="venue-feature-item">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                        <circle cx="9" cy="7" r="4"></circle>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                      </svg>
                      <div>
                        <span className="feature-label">Capacity</span>
                        <span className="feature-value">
                          {venue?.[0]?.capacity?.min} -{" "}
                          {venue?.[0]?.capacity?.max} guests
                        </span>
                      </div>
                    </div>

                    <div className="venue-feature-item">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                        <polyline points="9 22 9 12 15 12 15 22"></polyline>
                      </svg>
                      <div>
                        <span className="feature-label">Halls</span>
                        <span className="feature-value">
                          {venue?.[0]?.number_of_halls} halls
                        </span>
                      </div>
                    </div>
                    <div className="venue-feature-item">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                        <circle cx="12" cy="10" r="3"></circle>
                      </svg>
                      <div>
                        <span className="feature-label">Location</span>
                        <span className="feature-value">
                          {venue?.[0]?.city}
                        </span>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Amenities */}
                <section className="venue-section">
                  <h2 className="venue-section-title">Amenities</h2>
                  <div className="amenities-grid">
                    {normalizeAmenitiesList(venue?.[0]?.amenities).map(
                      (amenity, index) => (
                        <div key={index} className="amenity-item">
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                          <span>{amenity}</span>
                        </div>
                      )
                    )}
                  </div>
                </section>

                {/* CTA to add review at bottom of About */}
                {(() => {
                  const reviewsListForCta = venue?.[0]?.reviews_list ?? [];
                  const userAlreadyReviewedVenue = reviewsListForCta.some(
                    (r) => String(r.user_id) === String(userData?.id),
                  );
                  if (
                    isEndUserLoggedIn &&
                    !isBusinessLoggedIn &&
                    userAlreadyReviewedVenue
                  ) {
                    return (
                      <div className="about-add-review-cta about-add-review-cta-already">
                        <p>You have already posted a review for this venue.</p>
                      </div>
                    );
                  }
                  if(!isBusinessLoggedIn){
                    return (
                    <div className="about-add-review-cta">
                      <p>
                        Have you visited this venue? Share your experience and
                        help others decide.
                      </p>
                      <button
                        type="button"
                        className="cta-add-review-btn"
                        onClick={() => setActiveTab("reviews")}
                      >
                        Add a review
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                      </button>
                    </div>
                  );
                  }
                })()}
              </>
            )}

            {activeTab === "reviews" && (
              <section
                className="venue-section"
                id="venue-reviews-panel"
                role="tabpanel"
                aria-labelledby="venue-tab-reviews"
              >
                {/* Ratings & reviews summary */}
                {(() => {
                  const raw = filteredData?.[0];
                  const rating = raw?.average_rating ?? 0;
                  const reviewsCount = raw?.total_reviews ?? 0;
                  const reviewsList = raw?.reviews ?? [];
                  return (
                    <>
                      <div className="reviews-summary">
                        <div className="reviews-summary-stars" aria-hidden>
                          {[...Array(5)].map((_, i) => (
                            <svg
                              key={i}
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              className={i < Math.floor(rating) ? "" : "empty"}
                            >
                              <polygon
                                points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
                                fill="currentColor"
                              />
                            </svg>
                          ))}
                        </div>
                        <div>
                          <span className="reviews-summary-text">
                            {Number(rating).toFixed(1)} out of 5
                          </span>
                          <span className="reviews-summary-count">
                            {" "}
                            · {reviewsCount}{" "}
                            {reviewsCount === 1 ? "review" : "reviews"}
                          </span>
                        </div>
                      </div>

                      {/* List of reviews */}
                      {reviewsList.length > 0 ? (
                        <ul className="reviews-list">
                          {reviewsList.map((rev, idx) => {
                            const isCurrentUserReview =
                              String(rev.user_id) === String(userData?.id);
                            const reviewDate =
                              rev.date ||
                              (rev.created_at && formatDate(rev.created_at));
                            const reviewText = rev.text ?? rev.review;
                            return (
                              <li key={idx} className="reviews-list-item">
                                <div className="reviews-list-item-header">
                                  <div className="reviews-list-item-stars">
                                    {[...Array(5)].map((_, i) => (
                                      <svg
                                        key={i}
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        className={
                                          i < (rev.rating ?? 5) ? "" : "empty"
                                        }
                                      >
                                        <polygon
                                          points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
                                          fill="currentColor"
                                        />
                                      </svg>
                                    ))}
                                  </div>
                                  <span className="reviews-list-item-meta">
                                    {isCurrentUserReview ? (
                                      <>
                                        <span className="reviews-list-item-your">
                                          Your review
                                        </span>
                                        {reviewDate && (
                                          <span className="reviews-list-item-sep">
                                            {" "}
                                            ·{" "}
                                          </span>
                                        )}
                                      </>
                                    ) : rev.user_name || rev.username ? (
                                      <>
                                        <span className="reviews-list-item-by">
                                          Posted by-
                                        </span>
                                        <span className="reviews-list-item-username">
                                          {rev.user_name || rev.username}
                                        </span>
                                        {reviewDate && (
                                          <span className="reviews-list-item-sep">
                                            {" "}
                                            ·{" "}
                                          </span>
                                        )}
                                      </>
                                    ) : null}
                                    {reviewDate && (
                                      <span className="reviews-list-item-date">
                                        {reviewDate}
                                      </span>
                                    )}
                                  </span>
                                </div>
                                {reviewText && (
                                  <p className="reviews-list-item-text">
                                    {reviewText}
                                  </p>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <p className="reviews-empty">
                          No reviews yet. Be the first to add one!
                        </p>
                      )}

                      {/* Form or already-posted message */}
                      {(() => {
                        const userAlreadyReviewedVenue = reviewsList.some(
                          (r) => String(r.user_id) === String(userData?.id),
                        );
                      
                        if (
                          isEndUserLoggedIn &&
                          !isBusinessLoggedIn &&
                          userAlreadyReviewedVenue
                        ) {
                          return (
                            <div className="review-form-divider">
                              <h3 className="review-form-divider-title">
                                You have already posted a review for this venue
                              </h3>
                            </div>
                          );
                        }
                        if (!isBusinessLoggedIn) {
                          return (
                            <div className="review-form-divider">
                              <h3 className="review-form-divider-title">
                                Add a review
                              </h3>
                              <ReviewForm
                                entityType="venue"
                                entityId={venue?.[0]?.id}
                                entityName={venue?.[0]?.name}
                              />
                            </div>
                          );
                        }
                      })()}
                    </>
                  );
                })()}
              </section>
            )}
          </div>

          {/* Sidebar */}
          {!isBusinessLoggedIn && (
            <div className="venue-details-sidebar">
              <div className="venue-contact-card venue-enquiry-card">
                <h3 className="contact-card-title">
                  Interested in this venue?
                </h3>
                <p className="venue-enquiry-card-text">
                  Send an enquiry and the venue will get back to you.
                </p>
                <button
                  type="button"
                  className="venue-enquire-btn venue-enquire-btn-sidebar"
                  onClick={() =>
                    navigate("/enquiry?venueId=" + (venue?.[0]?.id ?? ""))
                  }
                >
                  Send enquiry
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Explore more cities – full-width carousel after review section */}
      <ExploreMoreCities
        currentCity={venue?.[0]?.city}
        onCitySelect={(city) => navigate(buildHomeCityPath(city))}
        autoScroll
      />

      {/* Image Modal */}
      {isImageModalOpen && (
        <div className="image-modal" onClick={() => setIsImageModalOpen(false)}>
          <div
            className="image-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="modal-close-btn"
              onClick={() => setIsImageModalOpen(false)}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            <img
              src={venue?.[0]?.images[selectedImageIndex]}
              alt={venue?.[0]?.name}
            />
            <button
              className="modal-nav-btn modal-prev"
              onClick={handlePrevImage}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>
            <button
              className="modal-nav-btn modal-next"
              onClick={handleNextImage}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default VenueDetailPage;
